import React, { useState, useEffect } from 'react';
import {
  Box, Card, Typography, CircularProgress, Chip, Menu, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';
import { Assignment as RequestsIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import client from '../../api/client';
import { toPersianDigits } from '../../utils/numberConverter';

const GRADIENT = 'linear-gradient(45deg, #1a237e 30%, #3949ab 90%)';

const STATUS_OPTIONS = [
  { value: 'registered', label: 'ثبت نام', color: 'info' },
  { value: 'in_progress', label: 'در حال انجام', color: 'warning' },
  { value: 'completed', label: 'تکمیل شده', color: 'success' },
];

const statusMeta = (value) =>
  STATUS_OPTIONS.find((s) => s.value === value) || STATUS_OPTIONS[0];

const formatShamsi = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString('fa-IR', {
      timeZone: 'Asia/Tehran', year: 'numeric', month: '2-digit', day: '2-digit',
    });
    const time = d.toLocaleTimeString('fa-IR', {
      timeZone: 'Asia/Tehran', hour: '2-digit', minute: '2-digit',
    });
    return `${date} - ${time}`;
  } catch (e) {
    return '-';
  }
};

const Requests = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await client.get('/users/admin/consultation-requests/');
      setItems(res.data.results || []);
    } catch (e) {
      toast.error('خطا در دریافت درخواست‌ها');
    } finally {
      setLoading(false);
    }
  };

  const openStatusMenu = (e, item) => {
    setSelected(item);
    setAnchorEl(e.currentTarget);
  };

  const changeStatus = async (newStatus) => {
    const item = selected;
    setAnchorEl(null);
    if (!item || item.status === newStatus) return;
    try {
      await client.patch(`/users/admin/consultation-requests/${item.id}/`, { status: newStatus });
      setItems((prev) =>
        prev.map((it) =>
          it.id === item.id
            ? { ...it, status: newStatus, status_display: statusMeta(newStatus).label }
            : it
        )
      );
      toast.success('وضعیت تغییر کرد');
    } catch (e) {
      toast.error('خطا در تغییر وضعیت');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const headCellSx = { color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap', borderBottom: 'none' };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <RequestsIcon sx={{ color: '#1a237e' }} />
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
          درخواست‌ها
        </Typography>
        <Chip
          label={`${toPersianDigits(items.length)} مورد`}
          size="small"
          sx={{ bgcolor: 'rgba(26,35,126,0.1)', color: '#1a237e', fontWeight: 'bold' }}
        />
      </Box>

      {items.length === 0 ? (
        <Card sx={{ p: 5, textAlign: 'center', borderRadius: 3 }}>
          <Typography color="text.secondary">درخواستی ثبت نشده است</Typography>
        </Card>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 20px rgba(26,35,126,0.08)' }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ '& th': headCellSx, background: GRADIENT }}>
                <TableCell>ردیف</TableCell>
                <TableCell>نام و نام خانوادگی</TableCell>
                <TableCell>نام فروشگاه</TableCell>
                <TableCell>شهر</TableCell>
                <TableCell>موبایل</TableCell>
                <TableCell>تاریخ ثبت</TableCell>
                <TableCell>وضعیت</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => {
                const meta = statusMeta(item.status);
                return (
                  <TableRow
                    key={item.id}
                    sx={{
                      '&:nth-of-type(odd)': { bgcolor: '#f7f9fd' },
                      '&:hover': { bgcolor: 'rgba(26,35,126,0.05)' },
                      transition: 'background-color 0.2s',
                    }}
                  >
                    <TableCell sx={{ color: 'text.secondary' }}>{toPersianDigits(index + 1)}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{item.full_name}</TableCell>
                    <TableCell>{item.shop_name}</TableCell>
                    <TableCell>{item.city}</TableCell>
                    <TableCell sx={{ direction: 'ltr', textAlign: 'right', fontFamily: 'monospace' }}>
                      {toPersianDigits(item.mobile)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                      {formatShamsi(item.created_at)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.status_display || meta.label}
                        color={meta.color}
                        size="small"
                        onClick={(e) => openStatusMenu(e, item)}
                        sx={{ cursor: 'pointer', fontWeight: 'bold', minWidth: 90, color: 'white' }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* منوی تغییر وضعیت */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {STATUS_OPTIONS.map((s) => (
          <MenuItem
            key={s.value}
            onClick={() => changeStatus(s.value)}
            selected={selected?.status === s.value}
            sx={{ justifyContent: 'center' }}
          >
            <Chip label={s.label} color={s.color} size="small" sx={{ cursor: 'pointer', color: 'white', minWidth: 90, fontWeight: 'bold' }} />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default Requests;